import type {CommerceAPI, ShopperBaskets, ShopperCustomers} from 'commerce-api'
import AlgoliaAnalytics, {InsightsMethodMap} from 'search-insights'
import {
  action,
  computed,
  flow,
  flowResult,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import {format} from 'date-fns'
import {isMatchingAddress, getDeviceType} from '../utils/utils'
import {DateUtils} from '../utils/date-utils'
import {RootStore} from './RootStore'
import {StoreModel, Wrapper} from './utils'
import {CustomerCredentials} from './CustomerStore' 
import {CheckoutSteps} from '../types/ui'
import {PaymentInstrument, Status, StatusMap} from '../types/commerce'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {daysOfTheWeek, months} from '../constants'
import {
  BasketPromotionsThreshold,
  BasketPromotionsThresholdResponse,
  BasketResult,
  BeforeYouGoPages,
  CustomerBasket,
  DeliverySlot,
  MissedOffers,
  OrderAddress,
  PartialProductItem,
  PostcodeDeliveryStatusResponse,
  PreviouslyRemovedItem,
  ReserveSlotResponse,
  StoreBasket,
  TimeSlot,
  TProductItem,
} from '../types/store/basket'
import {Cookies, LocalStorage} from '../utils/storages'
import {ALCOHOL_PRODUCT_CLASS, COOKIE_STORAGE_KEYS, LOCALSTORAGE_KEYS} from '../utils/constants'
import {StoreDetailResponseItem} from '../types/store/storeDetails'
import {Nullable} from '../types/utils'
import {SubmitOrderData} from '../types/store/basket'
import {CheckoutBonusCardStatus} from '../types/store/common'

const MissingBasketError = new Error('Missing basket')

type AddressFormType = 'add' | 'edit'
export class BasketStore implements StoreModel {
  api: CommerceAPI
  orderDiscount?: number
  rootStore: RootStore
  basket?: StoreBasket
  isGuestCheckout: boolean
  status: Status
  currentCheckoutStep: number
  deliverySlots: Nullable<DeliverySlot>
  nextAvailableSlot: Nullable<TimeSlot>
  deliverySlotPostalCode: Nullable<string>
  nearestStoreByDeliveryPostCode: Nullable<StoreDetailResponseItem>
  initMergingUI: boolean
  basketMergeData: string
  basketMergeType: string
  openMiniBasketUI: boolean
  beforeYouGoPages: Nullable<BeforeYouGoPages>
  missedOffers: Nullable<MissedOffers>
  promotionalThresholds: Array<BasketPromotionsThreshold>
  filteredPromotionalThresholds: Array<BasketPromotionsThreshold>
  bonusCardStatus: CheckoutBonusCardStatus
  isAppliedPostCodeDeliverable: Nullable<boolean>
  processingAppliedPostCode: boolean
  productsToUpdate: Array<PartialProductItem>
  productsToUpdateTimeout: ReturnType<typeof setTimeout>
  deliveryAddressFormExpanded : AddressFormType | null

  constructor(
    rootStore: RootStore,
    initialState?: ReturnType<RootStore['getState']>['basketStore']
  ) {
    this.basket = initialState?.basket
    this.isGuestCheckout = true
    this.status = StatusMap.INIT
    this.currentCheckoutStep = CheckoutSteps.CONTACT_INFO
    this.deliverySlots = null
    this.nextAvailableSlot = null
    this.deliverySlotPostalCode = null
    this.beforeYouGoPages = null
    this.missedOffers = null
    this.initMergingUI = false
    this.openMiniBasketUI = false
    this.basketMergeData = ''
    this.basketMergeType = ''
    this.promotionalThresholds = []
    this.filteredPromotionalThresholds = []
    this.nearestStoreByDeliveryPostCode = null
    this.bonusCardStatus = {}
    this.isAppliedPostCodeDeliverable = null
    this.processingAppliedPostCode = false
    this.productsToUpdate = []
    this.productsToUpdateTimeout = setTimeout(() => {})
    this.deliveryAddressFormExpanded = null

    makeObservable(this, {
      basket: observable,
      productsToUpdate: observable,
      initMergingUI: observable,
      openMiniBasketUI: observable,
      basketMergeData: observable,
      basketMergeType: observable,
      isGuestCheckout: observable,
      status: observable,
      currentCheckoutStep: observable,
      deliverySlots: observable,
      nextAvailableSlot: observable,
      deliverySlotPostalCode: observable,
      beforeYouGoPages: observable,
      missedOffers: observable,
      promotionalThresholds: observable,
      filteredPromotionalThresholds: observable,
      nearestStoreByDeliveryPostCode: observable,
      bonusCardStatus: observable,
      isAppliedPostCodeDeliverable: observable,
      processingAppliedPostCode: observable,
      loaded: computed,
      hasItems: computed,
      hasAlcoholItems: computed,
      productIds: computed,
      applicableShippingMethods: computed,
      count: computed,
      paymentMethod: computed,
      shippingAddress: computed,
      billingAddress: computed,
      orderTotal: computed,
      basketSubTotal: computed,
      bonusCardMax: computed,
      isBillingSameAsShipping: computed,
      deliveryPostalCode: computed,
      sameDayDeliveryAvailable: computed,
      currentDeliverySlotId: computed,
      currentDeliveryStoreId: computed,
      currentDeliverySlot: computed,
      currentDeliveryDay: computed,
      deliverySlotArray: computed,
      excludeSubstitutes: computed,
      isCarrierBagEditable: computed,
      isCarrierBagAdded: computed,
      carrierBagCharge: computed,
      addressDisplay: computed,
      deliveryDate: computed,
      minimumBasketValue: computed,
      basketCarrierBagCharge: computed,
      basketDeliveryCharge: computed,
      basketFreeDeliveryThreshold: computed,
      basketSmallBasketCharge: computed,
      basketSmallBasketChargeThreshold: computed,
      basketItemsPriceBeforeDiscount: computed,
      appliedPromocode: computed,
      subtotal: computed,
      canAddItems: computed,
      mergeBasketTotal: computed,
      mergeBasketCount: computed,
      runATBAnalytics: action.bound,
      setOpenMiniBasketUI: action.bound,
      updateBasketItemQuantity: flow.bound,
      removePreviouslyRemovedItem: flow,
      clearPreviouslyRemovedItems: flow.bound,
      getOrCreateBasket: flow.bound,
      checkoutLogin: flow.bound,
      addOrUpdateBillingAddress: flow.bound,
      addOrUpdateShippingAddress: flow.bound,
      getShippingMethods: flow.bound,
      setShippingMethod: flow.bound,
      setCustomerPaymentMethod: flow.bound,
      submitOrder: flow.bound,
      deleteCustomerPaymentMethod: flow.bound,
      setPromoCode: flow.bound,
      removePromoCode: flow.bound,
      updateBasketCurrency: flow,
      updateBasketSubstitutes: flow.bound,
      createSoftLoggedOffBasket: flow.bound,
      updateBasketDeliveryNote: flow.bound,
      updateCarrierBag: flow.bound,
      setBonusCardStatus: action.bound,
      setNearestStore: action.bound,
      slotTime: action.bound,
      setCustomerEmail: flow.bound,
      setGuestPostalCode: flow.bound,
      setPostCode: flow.bound,
      clearPostCode: flow.bound,
      getDeliverySlots: flow.bound,
      getNextAvailableSlot: flow.bound,
      reserveDeliverySlot: flow.bound,
      getBeforeYouGoPages: flow.bound,
      mergeBaskets: flow.bound,
      getMissedOffers: flow.bound,
      getBasketPromotionalThreshold: flow.bound,
      transformPromotionalThresholds: flow.bound,
      buildThresholdItem: action.bound,
      parkCurrentCartAndGetNewCart: flow.bound,
      retrieveParkedCartBack: flow.bound,
      handlePostcodeDeliveryStatus: flow.bound,
      clearSoftLogoutBasket: flow.bound,
      deliveryAddressFormExpanded: observable,
      expandDeliveryAddressForm: flow.bound,
      addProductToUpdate:action.bound
    })
    this.api = rootStore.api
    this.rootStore = rootStore

    // Determine if applied Post code to basket is deliverable
    reaction(
      () => ({
        basket: this.basket
      }),
      async () => {
        if (this.basket?.c_deliveryPostalCode) {
          const postcodeDeliveryStatus = await flowResult(
            this.handlePostcodeDeliveryStatus(this.basket.c_deliveryPostalCode)
          )

          runInAction(() => {
            this.isAppliedPostCodeDeliverable = postcodeDeliveryStatus
          })

          this.rootStore.globalStore.setSelectedStoreId()
          await flowResult(this.rootStore.globalStore.setSelectedRegion(postcodeDeliveryStatus))
        } else {
          runInAction(() => {
            this.isAppliedPostCodeDeliverable = null
          })
          this.rootStore.globalStore.setSelectedStoreId()
          this.rootStore.globalStore.setSelectedRegion()
        }
      }
    )

    reaction(
      () => ({
        length: this.productsToUpdate.length,
        quantity: this.productsToUpdate.map((product) => product.quantity),
      }),
      async () => {
        // Start the timer again if there is another change in quantity or length
        clearTimeout(this.productsToUpdateTimeout)

        if (this.productsToUpdate.length) {
          this.productsToUpdateTimeout = setTimeout(async () => {
            this.updateBasketItemQuantity(this.productsToUpdate)
            this.productsToUpdate = []
          }, 1000)
        }
      }
    )
  }

  get asJson() {
    return {
      basket: this.basket,
      status: this.status,
    }
  }

  get loaded() {
    return this.basket?.basketId != null
  }

  get hasItems() {
    if (!this.basket || !this.basket.productItems) {
      return false
    }
    return this.basket.productItems.length > 0
  }

  get hasAlcoholItems() {
    if (!this.basket || !this.basket.productItems) {
      return false
    }

    const {productStore} = this.rootStore

    return this.basket.productItems.some((productItem) => {
      const productId = productItem?.productId
      if (!productId) return false

      const productData = productStore.productsById[productId]

      if (!productData) return false

      return productData?.productClasses?.includes(ALCOHOL_PRODUCT_CLASS)
    })
  }

  get productIds() {
    return (this.basket?.productItems ?? []).reduce((acc, item) => {
      if (item.productId) {
        acc.push(item.productId)
      }
      return acc
    }, [] as string[])
  }

  get count() {
    return this.basket?.productItems?.reduce((a, b) => {
      return a + (b.quantity || 0)
    }, 0)
  }

  get paymentMethod() {
    return this.basket?.paymentInstruments && this.basket.paymentInstruments[0]
      ? this.basket.paymentInstruments[0]
      : null
  }

  get shippingAddress() {
    return this.basket?.shipments && this.basket?.shipments[0]
      ? this.basket?.shipments[0].shippingAddress
      : null
  }

  get applicableShippingMethods() {
    return this.basket?.applicableShippingMethods || null
  }

  get defaultShippingMethodId() {
    return this.basket?.defaultShippingMethodId || ''
  }

  get shippingMethod() {
    return this.basket?.shipments && this.basket?.shipments[0]
      ? this.basket?.shipments[0].shippingMethod
      : null
  }

  get billingAddress() {
    return this.basket?.billingAddress || null
  }

  get basketSubTotal() {
    return this.basket ? this.basket?.productSubTotal : 0
  }

  get minimumBasketValue() {
    const minDeliveryThreshold = this.filteredPromotionalThresholds?.find(x => x.thresholdSource === 'minDelivery')
    if(minDeliveryThreshold){
      return minDeliveryThreshold.thresholdValue
    } else {
      return 0
    }
  }

  get basketDeliveryCharge() {
    if(isNaN(this.basket?.shipments?.[0]?.shippingMethod?.c_deliveryCharge)){
      return 0
    } else {
      return this.basket?.shipments?.[0]?.shippingMethod?.c_deliveryCharge
    }
  }

  get basketCarrierBagCharge() {
    if (!this.basket?.c_isCarrierBagAdded) {
      return null
    }

    return this.basket?.shipments?.length
      ? this.basket?.shipments?.[0]?.shippingMethod?.c_carrierBagCharge ?? null
      : null
  }

  get basketFreeDeliveryThreshold(): number | null {
    return this.basket?.shipments?.length
      ? this.basket?.shipments?.[0]?.shippingMethod?.c_freeDeliveryThreshold ?? null
      : null
  }

  get basketSmallBasketCharge(): number | null {
    return this.basket?.shippingItems?.length
      ? this.basket?.shippingItems?.[0]?.c_smallBasketCharge ?? null
      : null
  }

  get basketSmallBasketChargeThreshold(): number | null {
    return this.basket?.shippingItems?.length
      ? this.basket?.shippingItems?.[0]?.c_smallBasketChargeThreshold ?? null
      : null
  }

  get basketItemsPriceBeforeDiscount(): number {
    if (!this.basket) return 0

    const productsPriceTotal =
      this.basket?.productItems?.reduce((total, product) => {
        if (!product.price) return total
        total = total + product.price
        return total
      }, 0) || 0

    const {orderPriceAdjustments} = this.basket

    const priceAdjustmentsTotal =
      orderPriceAdjustments?.reduce(
        (total, adjustment) => (total += Math.abs(adjustment.price ?? 0)),
        0
      ) || 0

    return productsPriceTotal + priceAdjustmentsTotal
  }

  get subtotal() {
    if (!this.basket || !this.hasItems) return 0

    return this.basket.productItems?.reduce((subtotal, {price}) => (subtotal += price ?? 0), 0) ?? 0
  }

  get bonusCardMax() {
    const {orderStore, customerStore} = this.rootStore
    const orderAmount = orderStore.editMode ? orderStore.orderEditOrderTotalWithoutBonusCard : this.orderTotal

    const maxBalance = Math.min(
      Number(customerStore?.bonusCardBalance ?? 0),
      Number(orderAmount ?? 0)
    )

    return maxBalance.toFixed(2) ?? '0'
  }

  get isBillingSameAsShipping() {
    if (!this.shippingAddress && !this.billingAddress) {
      return false
    }
    if (!this.shippingAddress) {
      return false
    }
    if (!this.billingAddress && this.shippingAddress) {
      return true
    }
    if (!this.billingAddress) {
      return false
    }
    const result = isMatchingAddress(this.billingAddress, this.shippingAddress)
    return result
  }

  get deliveryPostalCode() {
    return this.basket?.c_deliveryPostalCode
      ? this.basket?.c_deliveryPostalCode
      : this.rootStore.customerStore.primaryAddress?.postalCode
  }

  get sameDayDeliveryAvailable() {
    return !!this.deliverySlotArray?.[0]?.hasAvailableSlots
  }

  get currentDeliverySlotId() {
    return this.basket?.c_deliverySlotId
  }

  get currentDeliveryStoreId() {
    return this.basket?.c_deliveryStoreId || this.rootStore.customerStore.deliveryStoreId
  }

  get isCarrierBagEditable() {
    return this.basket?.c_isCarrierBagEditable
  }

  get isCarrierBagAdded() {
    return this.basket?.c_isCarrierBagAdded
  }

  get carrierBagCharge() {
    return this.basket?.shipments?.[0]?.shippingMethod?.c_carrierBagCharge ?? 0
  }

  get deliverySlotArray(): DeliverySlot[] {
    // Check if deliverySlots is not null
    if (this.deliverySlots) {
      // If deliverySlots is not null, safely use Object.values
      return Object.values(this.deliverySlots)
    } else {
      // If deliverySlots is null, return an empty array
      return []
    }
  }

  get excludeSubstitutes() {
    return this.basket?.c_excludeSubstitutes
  }

  get currentDeliverySlot() {
    const _slots = this.deliverySlotArray
    let currentSlot = {day: {} as DeliverySlot, timeSlot: null as TimeSlot | null}

    _slots.forEach(({slots}, idx) => {
      const matchingSlot = slots?.find(({id}) => id === this.currentDeliverySlotId)
      if (matchingSlot) {
        currentSlot = {
          timeSlot: matchingSlot,
          day: _slots[idx],
        }
      }
    })
    return currentSlot
  }

  get currentDeliveryDay() {
    const {orderStore} = this.rootStore
    const {timeSlot} = this.currentDeliverySlot
    const startTime = new Date(this.basket?.c_windowStartTime)
    let dayText = daysOfTheWeek[startTime?.getDay()]
    const isToday = new Date().getDay() === startTime.getDay()
    const isTomorrow = new Date().setDate(new Date().getDate() + 1) === startTime.getDate()
    const currentDate = new Date().getTime()
    const expiryTime = new Date(this.basket?.c_slotExpiryTime).getTime()
    const slotTimeLeft = expiryTime - currentDate

    if (isToday) {
      dayText = 'Today'
    } else if (isTomorrow) {
      dayText = 'Tomorrow'
    }

    // Checking if we are in EDIT MODE, and we haven't changed delivery, initial delivery is reserved.
    let isAvailable = (orderStore.editMode && !orderStore.orderEditDeliverySlotChanged) || false

    if (timeSlot?.available || slotTimeLeft > 0) {
      isAvailable = true
    }

    return {
      dayText,
      day: startTime?.getDate()?.toString(),
      month: months[startTime?.getMonth()],
      startHour: timeSlot?.startHour,
      endHour: timeSlot?.endHour,
      isAvailable,
    }
  }

  get deliveryDate() {
    const startTime = this.basket?.c_windowStartTime
    let date = ''
    if (startTime) {
      date = new Date(startTime)
        .toLocaleDateString('CA')
        .split('/')
        .map((datePart: string) => (datePart?.length === 1 ? `0${datePart}` : datePart))
        .join('/')
    }
    return date
  }

  get orderTotal() {
    return this.basket?.orderTotal
  }

  get addressDisplay() {
    const {address1, address2, city, postalCode} = this.shippingAddress || {}
    return [address1, address2, city, postalCode].filter(Boolean).join(', ')
  }

  get appliedPromocode() {
    return this.basket?.couponItems?.find(x => x.statusCode === "APPLIED") ?? null
  }

  get canAddItems() {
    return (
      this.basket?.c_deliveryStoreId ||
      this.rootStore.customerStore.deliveryStoreId ||
      typeof this.isAppliedPostCodeDeliverable === 'boolean'
    )
  }

  get mergeBasketTotal() {
    let total
    if (this.basketMergeType !== 'edit') {
      if (this.basket?.c_storedBasketJSON?.length) {
        try {
          const storedBasket = JSON.parse(this.basket?.c_storedBasketJSON)
          total = storedBasket?.basketTotal?.value
        } catch(e) {
          total = 0
        }
      }
    } else {
      total = this.basket?.productSubTotal || 0
    }
    return total
  }

  get mergeBasketCount() {
    let count = 0
    if (this.basketMergeType !== 'edit') {
      if (this.basket?.c_storedBasketJSON?.length) {
        try {
          const storedBasket = JSON.parse(this.basket?.c_storedBasketJSON)
          if('productLineItems' in storedBasket && storedBasket.productLineItems.length) {
            for(const x in storedBasket.productLineItems) {
              count += storedBasket.productLineItems[x].quantity
            }
          }
        } catch(e) {
          count = 0
        }
      }
    } else {
      count = this.count || 0
    }
    return count
  }

  runATBAnalytics(items: PartialProductItem[]) {
    /** Einstein Analytics events **/
    this.api.einstein.sendAddToCart({
      products: items.map((item) => {
        return {
          id: item.product?.id,
          quantity: item.quantity,
          price: item.product?.price ?? 0,
          sku: '',
        }
      }),
    })

    items.forEach((item) => {
      const {product, event} = item
      const queryID = product?.__queryID || product?.__autocomplete_queryID
      const fromSearchResult = Boolean(queryID)
      const settings = {
        method: fromSearchResult
          ? 'convertedObjectIDsAfterSearch'
          : ('convertedObjectIDs' as keyof InsightsMethodMap),
        params: {
          objectIDs: [product?.id],
          ...(fromSearchResult && {queryID: queryID}),
        },
      }

      /* Algolia Analytics events */
      if (event) {
        AlgoliaAnalytics(settings.method, {
          eventName: event,
          index: `${this.rootStore.globalStore.algoliaIndexBase}__products__default`,
          userToken: this.rootStore.customerStore.customerInfo?.customerId,
          ...settings.params,
        })
      }
    })
  }

  slotTime(dateStrUtc: string) {
    if (!dateStrUtc) return ''

    const zonedDate = DateUtils.utcToZonedDate(dateStrUtc)

    if (!DateUtils.validateDate(zonedDate)) {
      return ''
    }

    return format(zonedDate, 'HH:mm')
  }

  setOpenMiniBasketUI(open: boolean) {
    this.openMiniBasketUI = open
  }

  setBonusCardStatus(status: CheckoutBonusCardStatus) {
    this.bonusCardStatus = status
  }

  setNearestStore(storeDetail: StoreDetailResponseItem | null) {
    this.nearestStoreByDeliveryPostCode = storeDetail
  }

  addProductToUpdate(product: PartialProductItem) {
    this.productsToUpdate.push(product)
  }

  updateProductToUpdateProductQuantity(productToUpdateId: string | undefined, quantity: number) {
    if (!productToUpdateId) {
      return
    }
    this.productsToUpdate = this.productsToUpdate.map((product) => {
      if (product.product?.id === productToUpdateId) {
        return {
          ...product,
          quantity,
        }
      }
      return product
    })
  }

  getUpdateProductIndex(productId: string | undefined) {
    if (!productId) {
      return -1
    }
    return this.productsToUpdate.findIndex((prod) => prod.product?.id === productId)
  }

  *updateBasketItemQuantity(
    args: PartialProductItem | PartialProductItem[],
    openMiniBasket?: boolean
  ) {
    const productsForAnalytics: PartialProductItem[] = []

    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    // If we're already pending a request, no need to fire again
    if (this.status === 'pending') {
      return
    }

    this.status = 'pending'

    // Allow method to accept both single or multiple (array) items
    const items = Array.isArray(args) ? args : [args]

    const isBasketEmptyBeforeUpdate = this.basket.productItems?.length === 0
    const addedFromSearchDropdown = isBasketEmptyBeforeUpdate
      ? items.find((item) => item.isSearch)
      : false

    try {
      const itemPayload = items
        .filter((item) => item.product)
        .map((item) => {
          const existing = this.basket?.productItems?.find((basketItem) => {
            return basketItem.productId === item.product?.id
          })
          if (!existing) {
            productsForAnalytics.push(item)
          }
          return {
            productId: item.product?.id,
            quantity: item.quantity,
          }
        })

      const itemsToAdd = {
        body: itemPayload,
      }
      const result: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-updateBasketItemQuantity`,
        {
          method: 'POST',
          body: JSON.stringify(itemsToAdd.body),
        }
      )
      if (!result.ok) throw new Error('Product not available to add to basket')

      const json: {
        success: boolean
        basket: ShopperBaskets.Basket
        incompleteOffers: MissedOffers
      } = yield result.json()

      this.status = 'idle'

      // After we successfully added 1st item in the empty cart we need to open drawer.
      if (json.success) {
        // Update the products in the basket
        this.getOrCreateBasket(json.basket, json.incompleteOffers)

        // Run analytics if ATB was successful and a new product has been added
        if (productsForAnalytics.length) {
          this.runATBAnalytics(productsForAnalytics)
        }

        if (
          this.basket.productItems?.length === 1 &&
          isBasketEmptyBeforeUpdate
        ) {
          if (!openMiniBasket && 
            !addedFromSearchDropdown
            ) {
            this.openMiniBasketUI = true
          }

        }
        // Clear recent order
        this.rootStore.orderStore.recentOrder = null
      }

      return json
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *removePreviouslyRemovedItem(itemId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-RemovePreviouslyRemovedItem?pid=${itemId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      if (!res.ok) {
        throw new Error('Error removing previously saved item')
      } else {
        const json: {items?: {}} = yield res.json()
        if (json?.items) {
          this.basket.c_previouslyRemovedItems = json.items
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  *clearPreviouslyRemovedItems() {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-ClearPreviouslyRemovedItems`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      if (!res.ok) {
        throw new Error('Error clearing previously removed items')
      } else {
        const json: {items?: {}} = yield res.json()
        if (json?.items) {
          this.basket.c_previouslyRemovedItems = json.items
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  /**
   * Fetches customer baskets and stores the most recent. If none exist, a new basket is created.
   */
  *getOrCreateBasket(basket: ShopperBaskets.Basket, incompleteOffers?: MissedOffers, customerId = this.rootStore.customerStore?.customerInfo?.customerId || undefined) {
    try {
      if (!customerId) {
        throw new Error('Missing customer ID')
      }
      // Make sure basketMergeData is cleared when the basket is set
      this.basketMergeData = ''

      let customerBasket: ShopperBaskets.Basket | BasketResult | undefined = basket

      // It should never hit this a basket is required to be passed in!
      // This has been left in as a fallback if basket for whatever reason comes back from the backend as undefined
      if (!customerBasket) {
        // FYI - Using the API may cause some differences between the basket in the backend!
        // Fetch all baskets for customer
        const result: BasketResult = yield this.api.shopperCustomers.getCustomerBaskets({
          parameters: {customerId},
        })

        customerBasket = result.baskets?.[0]
      }

      let transformedBasket
      if (customerBasket?.basketId) {
        transformedBasket = transformBasket(this, customerBasket)
        // this.getMissedOffers(incompleteOffers) // No need to go fetch this stuff again if we've just had a response from the BE for it already
        if(incompleteOffers) {
          this.missedOffers = incompleteOffers
        }

      } else {
        // Customer has no baskets, create one and save it
        const newBasket: CustomerBasket = yield this.api.shopperBaskets.createBasket({body: {}})
        transformedBasket = transformBasket(this, newBasket)
      }

      this.basket = transformedBasket

      // Set postalcode cookie for guest session
      if (this.deliveryPostalCode) {
        Cookies.set(COOKIE_STORAGE_KEYS.DW_POSTALCODE, this.deliveryPostalCode, {expires: 31})
      } else {
        Cookies.remove(COOKIE_STORAGE_KEYS.DW_POSTALCODE)
      }

      // Set some background variables here for PriceBooks
      // Set SelectedStoreId
      this.rootStore.globalStore.setSelectedStoreId()
      // Set the selected Stores Region
      this.rootStore.globalStore.setSelectedRegion()
      // Set price day
      this.rootStore.globalStore.setSelectedPriceDay()
      // Set Availability day
      this.rootStore.globalStore.setSelectedAvailabilityDay()
      // Set some background variables here for PriceBooks

      // Ensure the selected app currency matches the basket currency
      const currency = this.rootStore.globalStore.currency

      if (currency && this.basket?.currency !== currency && this.basket?.basketId) {
        yield this.updateBasketCurrency(currency, this.basket.basketId)
      }

      yield this.getBasketPromotionalThreshold()

      // Basket is loaded, so UI can render most of the basket now
      this.status = 'idle'

      // Kick off a fetch of product details for each item. We need this to render
      // a cart item in the basket, since the basket item does not include an image
      // or any information about the variation values.
      if (this.basket?.productItems) {
        const productIds = this.basket?.productItems.map((item) => item.productId || '')
        this.rootStore.productStore.fetchProducts(productIds, false)
      }

      // Kick off a fetch of product details for each item within previously removed. We need this to render
      // a previously removed item in the basket, since the  item does not include an image
      // or any information about the variation values.
      if (this.basket?.c_previouslyRemovedItems) {
        let prevRemovedItems = []
        try {
          prevRemovedItems = JSON.parse(this.basket.c_previouslyRemovedItems) || []
          this.basket.c_previouslyRemovedItems = prevRemovedItems
        } catch (err) {
          this.basket.c_previouslyRemovedItems = []
        }
        if (this.basket.c_previouslyRemovedItems.length) {
          const prevRemovedIds = this.basket?.c_previouslyRemovedItems.map(
            (item: PreviouslyRemovedItem) => item.pid || ''
          )
          this.rootStore.productStore.fetchProducts(prevRemovedIds, false)
        }
      }

      // Once we created a new basket we don't have shipment address,
      // calling OCAPI to set primary saved user's address.
      if (!this.shippingAddress && this.rootStore.customerStore.isRegistered) {
        if (this.rootStore.customerStore.addresses?.length) {
          const shippingAddress = this.rootStore.customerStore.addresses.find(
            (address) => address.preferred
          )
          if (shippingAddress)
            yield this.addOrUpdateShippingAddress(shippingAddress as OrderAddress, false)
        }
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *updateBasketCurrency(currency: string, basketId: string) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: {currency},
        parameters: {basketId},
      })

      this.basket = transformBasket(this, updatedBasket)
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *updateBasketDeliveryNote(note: string) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: {c_deliveryNotes: note},
        parameters: {basketId: this.basket?.basketId},
      })
      this.basket = transformBasket(this, updatedBasket)
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *updateBasketSubstitutes(excludeSubstitutes: boolean) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: {c_excludeSubstitutes: excludeSubstitutes},
        parameters: {basketId: this.basket?.basketId},
      })

      this.basket = transformBasket(this, updatedBasket)
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *updateCarrierBag(includeBag = true) {
    try {
      const updatedBasket: CustomerBasket = yield this.api.shopperBaskets.updateBasket({
        body: {c_isCarrierBagAdded: includeBag},
        parameters: {basketId: this.basket?.basketId},
      })
      this.basket = transformBasket(this, updatedBasket)
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *createSoftLoggedOffBasket(registeredUserBasket: StoreBasket, email: string) {
    if (!registeredUserBasket) return

    try {
      this.status = StatusMap.PENDING

      const {customerInfo, ...basket} = registeredUserBasket

      const body = {
        ...sanitizeOCAPIBasketFromIncorrectProperties(basket),
        customerInfo: {
          email,
          customerId: customerInfo?.customerId,
        },
      }
      const createdBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
        body,
      })

      this.basket = transformBasket(this, createdBasket)
      this.status = StatusMap.DONE
    } catch (error) {
      this.status = StatusMap.ERROR
    }
  }

  *parkCurrentCartAndGetNewCart(basketProperties?: Partial<ShopperBaskets.Basket>) {
    if (!this.basket || !LocalStorage.available) return

    try {
      this.status = StatusMap.PENDING
      LocalStorage.remove(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART)

      const storingBasket: string = yield JSON.stringify(
        sanitizeOCAPIBasketFromIncorrectProperties({...this.basket})
      )
      // Saving active user's cart to storage, will retrieve this once transaction is done.
      LocalStorage.set(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART, storingBasket)

      yield this.api.shopperBaskets.deleteBasket({
        parameters: {
          basketId: this.basket.basketId!,
        },
      })

      const newBasketBody: Partial<ShopperBaskets.Basket> =
        sanitizeOCAPIBasketFromIncorrectProperties(basketProperties || {})

      if (this.rootStore.customerStore.isRegistered) {
        newBasketBody.customerInfo = {email: this.rootStore.customerStore?.customerInfo?.email!}
      }

      const newBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
        parameters: {},
        body: newBasketBody,
      })

      const readyBasket = transformBasket(this, newBasket)

      if (basketProperties?.c_deliveryPostalCode) {
        readyBasket.c_deliveryPostalCode = basketProperties.c_deliveryPostalCode
      }

      this.basket = readyBasket
      this.status = StatusMap.DONE

      return readyBasket
    } catch (error) {
      console.error("Error has occurred during parking user's basket.")
      this.status = StatusMap.ERROR
      return null
    }
  }

  *retrieveParkedCartBack() {
    if (!LocalStorage.available || !this.basket) return

    try {
      let basketToReturn: StoreBasket = {}
      this.status = StatusMap.PENDING
      const existedParkedBasket = LocalStorage.get(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART)

      if (existedParkedBasket) {
        const {basketId, c_orderNoBeingEdited, ...basket} = yield JSON.parse(existedParkedBasket)

        yield this.api.shopperBaskets.deleteBasket({
          parameters: {
            basketId: this.basket.basketId!,
          },
        })

        const newBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
          parameters: {},
          // We shouldn't get editMode parked basket, so create an empty one, otherwise use parked basket properties.
          body: c_orderNoBeingEdited ? {} : basket,
        })

        basketToReturn = transformBasket(this, {
          ...newBasket,
        })

        if (basketToReturn.productItems?.length) {
          this.rootStore.productStore.fetchProducts(
            basketToReturn.productItems
              .map((product) => product.productId)
              .filter((id): id is string => !!id)
          ), false
        }

        LocalStorage.remove(LOCALSTORAGE_KEYS.PARKED_CUSTOMER_CART)
        // If User cleared session and parked basket is absent, creating an empty one
      } else {
        yield this.api.shopperBaskets.deleteBasket({
          parameters: {
            basketId: this.basket.basketId!,
          },
        })
        const emptyBasket: StoreBasket = yield this.api.shopperBaskets.createBasket({
          parameters: {},
          body: {},
        })
        basketToReturn = transformBasket(this, emptyBasket)
      }

      this.basket = basketToReturn
      this.status = StatusMap.DONE

      return basketToReturn
    } catch (error) {
      console.error("Error has occurred during getting parked user's basket.")
      this.status = StatusMap.ERROR
    }
  }

  *checkoutLogin(credentials: {email: string; password?: string}) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    try {
      if ('password' in credentials) {
        yield this.rootStore.customerStore.loginCustomer(credentials as CustomerCredentials)
      }
      const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
        body: {email: credentials.email},
        parameters: {basketId: this.basket?.basketId},
      })
      this.basket = transformBasket(this, result)
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *updateCustomerInfo(info: ShopperBaskets.CustomerInfo) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
      body: info,
      parameters: {basketId: this.basket.basketId},
    })
    this.basket = transformBasket(this, result)
  }

  *addOrUpdateBillingAddress(address: OrderAddress) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    const {
      id,
      preferred,
      creationDate,
      lastModified,
      addressId,
      addressName,
      c_updateHistory,
      _resource_state,
      __type,
      _v,
      ...body
    } = address
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateBillingAddressForBasket({
        body,
        parameters: {basketId: this.basket?.basketId},
      })
      this.basket = transformBasket(this, {...this.basket, ...result})
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *addOrUpdateShippingAddress(address: OrderAddress, saveAddress = true) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    try {
      const {
        id,
        preferred,
        creationDate,
        lastModified,
        addressId,
        addressName,
        c_updateSource,
        c_updateHistory,
        _resource_state,
        __type,
        _v,
        ...body
      } = address
      const result: CustomerBasket = yield this.api.shopperBaskets.updateShippingAddressForShipment(
        {
          body: {
            ...body,
            /* We need c_customerAddressID for association with customer's address,
            because shipping one has auto-generated server side id */
            c_customerAddressID: addressId,
          },
          parameters: {
            basketId: this.basket?.basketId,
            shipmentId: 'me',
            useAsBilling: true,
          },
        }
      )
      this.basket = transformBasket(this, {...this.basket, ...result})
      this.getShippingMethods()

      if (this.rootStore.customerStore?.isRegistered && saveAddress) {
        if (!addressId) return
        const existedAddress = this.rootStore.customerStore.addresses.find(
          (customerAddress) => customerAddress.addressId === addressId
        )

        if (existedAddress) {
          this.rootStore.customerStore.updateCustomerAddress({
            ...body,
            addressId,
            preferred,
            c_updateSource,
          } as ShopperCustomers.CustomerAddress)
        } else {
          this.rootStore.customerStore.createCustomerAddress({
            ...body,
            preferred,
            addressId,
            c_updateSource,
          } as ShopperCustomers.CustomerAddress)
        }
      }
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *getShippingMethods() {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    try {
      const result: ShopperBaskets.ShippingMethodResult =
        yield this.api.shopperBaskets.getShippingMethodsForShipment({
          parameters: {basketId: this.basket?.basketId, shipmentId: 'me'},
        })

      if (this.basket) {
        this.basket.applicableShippingMethods = result.applicableShippingMethods
        this.basket.defaultShippingMethodId = result.defaultShippingMethodId
      }

      if (
        result.applicableShippingMethods &&
        result.applicableShippingMethods[0] &&
        !this.shippingMethod
      ) {
        this.setShippingMethod(result.applicableShippingMethods[0].id)
      }
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *setShippingMethod(id: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateShippingMethodForShipment({
        body: {id},
        parameters: {basketId: this.basket?.basketId, shipmentId: 'me'},
      })
      this.basket = transformBasket(this, {...this.basket, ...result})
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *submitOrder(
    submissionData: SubmitOrderData,
    paymentType: Nullable<string>
  ) {
    this.status = 'paymentPending'

    const {
      editMode = false,
      orderEditOrderTotal,
      isPaymentRefunding = false,
    } = this.rootStore.orderStore

    const orderTotal = this.orderTotal || 0
    const orderEditTotal = orderEditOrderTotal || 0

    const {
      btData,
      userData,
      threeDSecureData,
      usingSavedCard,
      saveCreditCard,
      saveNewAddress,
      billingAddress,
    } = submissionData

    try {
      const paymentData = {
        btData,
        userData,
        threeDSecureData,
        paymentType,
        usingSavedCard,
        billingAddress,
      }

      const {bonusCardUsed, leftToPay, hidePayment} = this.bonusCardStatus

      let bonusCardAmount = 0

      let paymentAmount = !editMode ? orderTotal : orderEditOrderTotal

      if (bonusCardUsed) {
        if (hidePayment) {
          bonusCardAmount = !editMode ? orderTotal : orderEditTotal
          paymentAmount = 0
        }

        if (leftToPay != null && leftToPay > 0) {
          bonusCardAmount = !editMode ? orderTotal : orderEditTotal
          paymentAmount = leftToPay
        }
      }

      const body = {
        'bonus-card-amount': bonusCardAmount,
        dwfrm_billing_save: false,
        dwfrm_billing_addressList: '',
        dwfrm_billing_billingAddress_addressFields_title: billingAddress?.title,
        dwfrm_billing_billingAddress_addressFields_firstName: billingAddress?.firstName,
        dwfrm_billing_billingAddress_addressFields_lastName: billingAddress?.lastName,
        dwfrm_billing_billingAddress_addressFields_customerAddressID: '',
        dwfrm_billing_billingAddress_addressFields_postal: billingAddress?.postalCode,
        dwfrm_billing_billingAddress_addressFields_address1: billingAddress?.address1,
        dwfrm_billing_billingAddress_addressFields_address2: billingAddress?.address2,
        dwfrm_billing_billingAddress_addressFields_city: billingAddress?.city,
        dwfrm_billing_billingAddress_addressFields_county: billingAddress?.stateCode,
        dwfrm_billing_billingAddress_addressFields_phone: billingAddress?.phone?.replace(' ', ''),
        dwfrm_billing_billingAddress_addToAddressBook: saveNewAddress ?? false,
        deviceType: getDeviceType(window?.navigator?.userAgent),
        dwfrm_billing_billingAddress_email_emailAddress:
          this.basket?.customerInfo?.email || this.rootStore.customerStore.customerInfo?.email,
        dwfrm_billing_billingAddress_addressFields_country: 'gb',
        paymentAmount: paymentAmount,
        customerNo: this.basket?.customerInfo?.customerNo || '',
        ...((hidePayment || isPaymentRefunding || (editMode && orderEditOrderTotal === 0)) && {
          noPaymentNeeded: true,
        }),
        ...(paymentType === 'PayPal' && {
          paypalReferrer: getAppOrigin().replace('http://', '').replace('https://', ''),
          braintreePaypalNonce: paymentData?.btData?.nonce,
        }),
        ...(!isPaymentRefunding
          ? {
              braintreeUseSavedCard: paymentData?.usingSavedCard,
              braintreeSaveCreditCard: saveCreditCard ?? false,
              // TODO value needs to be set by 'Make default' input - should be visible when checking out with new card & vault mode turned on
              braintreeCreditCardMakeDefault: false,
              dwfrm_creditcard_owner: '',
              dwfrm_creditcard_type: paymentData?.threeDSecureData?.details?.cardType || '',
              dwfrm_creditcard_number: `************${paymentData?.threeDSecureData?.details.lastFour}`,
              braintreePaymentMethodNonce:
              paymentData?.threeDSecureData?.nonce || paymentData?.btData?.nonce || '',
              braintreeDeviceData: JSON.stringify(paymentData?.userData),
              dwfrm_billing_paymentMethods_creditCard_type:
              paymentData?.threeDSecureData?.details?.cardType || '',
              dwfrm_billing_paymentMethods_creditCard_number: paymentData?.threeDSecureData?.details?.lastFour,
              dwfrm_billing_paymentMethods_creditCard_owner: '',
              'cc-expiration-date': '',
              dwfrm_billing_paymentMethods_creditCard_expiration_month: '',
              dwfrm_billing_paymentMethods_creditCard_expiration_year: '',
              dwfrm_billing_paymentMethods_creditCard_cvn: '',
              ...(!hidePayment && {
                dwfrm_billing_paymentMethods_selectedPaymentMethodID: paymentData?.paymentType,
              })}
          : {}),
      }

      const formBody = []
      for (const property in body) {
        const encodedKey = encodeURIComponent(property)
        const encodedValue = encodeURIComponent(
          body[property as keyof typeof body] as string | number
        )
        formBody.push(encodedKey + '=' + encodedValue)
      }

      const result: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Checkout-CreateOrder`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'},
          body: formBody.join('&'),
        }
      )

      if (!result.ok) {
        throw new Error('Error during placing the order')
      }

      const resultJson: {order_created: boolean, basket: ShopperBaskets.Basket, [key: string]: unknown, c_customerGroups: string[]} = yield result.json()

      this.status = 'idle'
      if (resultJson.order_created) { 
        this.getOrCreateBasket(resultJson.basket)
        if (resultJson.c_customerGroups) {
          this.rootStore.customerStore.setCustomerGroups(resultJson.c_customerGroups)
        }
      }
      return resultJson
    } catch (error) {}
  }

  *setCustomerPaymentMethod(paymentMethod: ShopperBaskets.BasketPaymentInstrumentRequest) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    const {paymentInstrumentId, ...data} = paymentMethod
    let result: CustomerBasket
    this.status = 'pending'
    const existingPaymentInstrumentId =
      this.basket?.paymentInstruments && this.basket.paymentInstruments[0]?.paymentInstrumentId

    let paymentInstrument

    if (!paymentInstrumentId) {
      const expiry = data?.expiry?.split('/')
      const expirationMonth = expiry?.[0]
      const expirationYear = expiry?.[1]
      delete data.expiry

      paymentInstrument = {
        paymentMethodId: 'CREDIT_CARD',
        paymentCard: {
          ...data,

          // /** @todo - this field does not exist in the request type, using maskedNumber for now */
          number: data.number?.replace(/ /g, ''),
          // maskedNumber: data.number?.replace(/ /g, ''),

          cardType: data.cardType,
          expirationMonth: parseInt(expirationMonth as string),
          expirationYear: parseInt(`20${expirationYear}`),

          // TODO: These fields are required for saving the card to the customer's
          // account. Im not sure what they are for or how to get them, so for now
          // we're just passing some values to make it work. Need to investigate.
          issueNumber: '',
          validFromMonth: 1,
          validFromYear: 2020,
        },
      }
    } else {
      paymentInstrument = {
        customerPaymentInstrumentId: paymentInstrumentId,
      }
    }

    try {
      result = yield this.api.shopperBaskets.addPaymentInstrumentToBasket({
        body: paymentInstrument,
        parameters: {basketId: this.basket?.basketId},
      })
      if (existingPaymentInstrumentId) {
        result = yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket?.basketId,
            paymentInstrumentId: existingPaymentInstrumentId,
          },
        })
      }
      if (this.rootStore.customerStore?.isRegistered && !paymentInstrumentId) {
        this.rootStore.customerStore.addCustomerPaymentInstrument(
          paymentInstrument as ShopperCustomers.CustomerPaymentInstrumentRequest
        )
      }
      if (this.basket) {
        this.basket.paymentInstruments = result.paymentInstruments as PaymentInstrument[]
      }
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *deleteCustomerPaymentMethod(paymentInstrumentId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }
    this.status = 'pending'
    try {
      const result: CustomerBasket =
        yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket?.basketId,
            paymentInstrumentId: paymentInstrumentId,
          },
        })
      if (this.basket) {
        this.basket.paymentInstruments = result.paymentInstruments as PaymentInstrument[]
      }
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *setPromoCode(promoCode: string) {

    try {

      if (!this.basket?.basketId) {
        throw MissingBasketError
      }
  
      if (!this.basket?.basketId) {
        throw MissingBasketError
      }

      /* Protects applying of new promocode if we have already one in the basket. Max 1 promocode per basket */
      if (this.appliedPromocode?.statusCode === 'APPLIED') {
        throw new Error(
          'You already applied a promo code. Remove the previous promo code to apply new one.'
        )
      }
      
      this.status = StatusMap.PENDING

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-ApplyCoupon?&coupon=${promoCode}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: {couponError?: string, basket: ShopperBaskets.Basket, error: boolean} = yield res.json()

      if (!res.ok || json?.couponError || !json?.basket) {
        throw new Error('Error applying promo code: ' + promoCode)
      }

      this.getOrCreateBasket(json.basket)

      if (json?.basket?.couponItems && Array.isArray(json.basket.couponItems)) {
        const [appliedCoupon] = json.basket.couponItems
        if (!appliedCoupon.valid || appliedCoupon.statusCode !== "APPLIED") {
          throw new Error(`Coupon code "${promoCode}" is not valid.`)
        }
      } else {
        throw new Error(
          'Error occurred during applying promo code to basket. No coupon items in the basket response.'
        )
      }
      this.status = StatusMap.IDLE
      return {error: false, message: null}
    } catch (error) {
      console.error(error)
      this.status = StatusMap.ERROR
      return {error: true, message: `Coupon code "${promoCode}" is not applied.`}
    }
  }

  *removePromoCode(couponItemId: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    this.status = StatusMap.PENDING
    
    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-RemoveCoupon?&couponid=${couponItemId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      )

      const json: {basket: ShopperBaskets.Basket} = yield res.json()

      if (!res.ok || !json?.basket) {
        throw new Error('Error removing promo code: ' + couponItemId)
      }

      this.getOrCreateBasket(json.basket)

      this.status = StatusMap.IDLE
    } catch (error) {
      console.error('Unable to remove promo code', error)
      this.status = StatusMap.ERROR
    }
  }

  *setCustomerEmail(email: string) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    this.status = 'pending'
    try {
      const result: CustomerBasket = yield this.api.shopperBaskets.updateCustomerForBasket({
        body: {email},
        parameters: {basketId: this.basket?.basketId},
      })
      this.basket = transformBasket(this, result)
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  // DF TODO: type the paymentInstrument parameter
  *setPayPalPaymentMethod(paymentInstrument: any) {
    if (!this.basket?.basketId) {
      throw MissingBasketError
    }

    let result: CustomerBasket
    this.status = 'pending'

    const existingPaymentInstrumentId =
      this.basket.paymentInstruments && this.basket.paymentInstruments[0]?.paymentInstrumentId

    try {
      result = yield this.api.shopperBaskets.addPaymentInstrumentToBasket({
        body: paymentInstrument,
        parameters: {basketId: this.basket.basketId},
      })
      if (existingPaymentInstrumentId) {
        result = yield this.api.shopperBaskets.removePaymentInstrumentFromBasket({
          parameters: {
            basketId: this.basket.basketId,
            paymentInstrumentId: existingPaymentInstrumentId,
          },
        })
      }
      // DF TODO: This typecast might be dodgy!
      this.basket.paymentInstruments = (result.paymentInstruments as PaymentInstrument[]) ?? []
      this.status = 'idle'
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *setGuestPostalCode() {
    const guestPostalCode = Cookies.get(COOKIE_STORAGE_KEYS.DW_POSTALCODE)
    if (guestPostalCode) {
      yield this.setPostCode(guestPostalCode)
    } else {
      yield this.clearPostCode()
    }
  }

  *setPostCode(postcode: string) {
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-SetPostalCode`,
      {
        method: 'POST',
        body: JSON.stringify({
          postcode: postcode
        }),
        credentials: 'include'
      }
    )
    const {basket, c_customerGroups} = yield response.json()

    if (basket) {
      yield this.getOrCreateBasket(basket)
    }
    if (c_customerGroups) {
      this.rootStore.customerStore.setCustomerGroups(c_customerGroups)
    }
    this.rootStore.globalStore.setDataLoadedForAccuratePricingAndAvailabilty()
    return postcode
  }

  *clearPostCode() {
    try {
      const response: {success: boolean, basket: CustomerBasket, c_customerGroups?: string[]} = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-ClearPostalCode`,
        {
          method: 'POST',
          credentials: 'include',
        }
      ).then((res) => res.json())

      if (!response.success) {
        throw new Error()
      }

      this.getOrCreateBasket(response.basket)
      this.rootStore.globalStore.setDataLoadedForAccuratePricingAndAvailabilty()
      if (response.c_customerGroups) {
        this.rootStore.customerStore.setCustomerGroups(response.c_customerGroups)
      }
      Cookies.remove(COOKIE_STORAGE_KEYS.DW_POSTALCODE)
      this.setNearestStore(null)
    } catch (error) {
      console.error('Error clearing postal code', error)
    }
  }

  /**
   * Geocode the delivery postcode and assign the nearest store.
   * @param postcode string postcode to use for search
   * @returns nothing
   */
  async assignNearestStore(postcode: string) {
    try {
      console.log(`Attempting to find nearest store to ${postcode} using google`)
      
      if (!postcode) { 
        this.setNearestStore(null)
        return
      }

      const cachedStore = sessionStorage.getItem('nearestStore')
      const cachedPostal = sessionStorage.getItem('nearestStorePostal')

      if (cachedStore && cachedPostal === postcode) {
        this.setNearestStore(JSON.parse(cachedStore))
        console.log(`Nearest store to ${postcode} set from cache`)
        return
      }
        
      const coords = await flowResult(this.rootStore.storeDetailsStore.getAddressCoordinates(postcode))
        
      if (coords) {
        const nearestStore = await flowResult(this.rootStore.storeDetailsStore.findNearestStoreByCoords(coords))
        if (nearestStore) {
          sessionStorage.setItem('nearestStore', JSON.stringify(nearestStore))
          sessionStorage.setItem('nearestStorePostal', postcode)
        }
        this.setNearestStore(nearestStore)
      }
    } catch(e) { 
      console.error(`Unable to assign a nearest store for postcode ${postcode}. using google`, e)
      this.setNearestStore(null)
    }
  }

  *getDeliverySlots() {
    try {
      this.deliverySlotPostalCode = this.deliveryPostalCode

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliverySlotsJSON?sameDay=true`,
        {credentials: 'include'}
      )
      const json: {slots?: DeliverySlot} = yield res.json()
      if (json?.slots) {
        this.deliverySlots = json.slots
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *getNextAvailableSlot() {
    try {
      this.deliverySlotPostalCode = this.deliveryPostalCode
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetNextAvailableDelivery?sameDay=true`,
        {credentials: 'include'}
      )
      const json: {slot?: TimeSlot} = yield res.json()
      if (json?.slot) {
        this.nextAvailableSlot = json.slot
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *reserveDeliverySlot(slotId: string) {
    try {
        const formData = new FormData()
        formData.append('shopping', 'true')
        formData.append('slotId', slotId)
        const res: Response = yield fetch(
            `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-ReserveSlot?slotId=${slotId}`,
            {
                method: 'POST',
                credentials: 'include',
                body: formData,
            }
        )

        if (!res.ok) {
            const result: ReserveSlotResponse = {
                result: { code: '400' }
            }
            return result
        }

        const json: ReserveSlotResponse = yield res.json()

        this.status = 'idle'
        const code = json?.result?.code
        if (code && code !== 'success') {
            // Handle the non-success code
            const result: ReserveSlotResponse = {
                result: { code }
            };
            return result
        }
        // Updates the products in the basket
        if (code && code === 'success') {
            if (json.basket) {
                this.getOrCreateBasket(json.basket, json.incompleteOffers)
            }
            if (json.c_customerGroups) {
                this.rootStore.customerStore.setCustomerGroups(json.c_customerGroups)
            }
        }

        return json
    } catch (error) {
        const result: ReserveSlotResponse = {
            result: { code: '300' }
        }
        this.status = 'error'
        console.error(error)
        return result
    }
}

  // Fetch the Before you go Pages
  *getBeforeYouGoPages(forceUpdate: boolean) {
    try {
      if (!this.beforeYouGoPages || forceUpdate) {
        const res: Response = yield fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/BeforeYouGo-AllPages`,
          {credentials: 'include'}
        )
        const json: {pages?: BeforeYouGoPages} = yield res.json()
        if (json?.pages && Array.isArray(json?.pages)) {
          this.beforeYouGoPages = json.pages
        }
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  *getMissedOffers(incompleteOffers?: MissedOffers) {
    if (incompleteOffers) {
      this.missedOffers = incompleteOffers
      return
    }
    try {
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-GetIncompleteOffers`,
        {credentials: 'include'}
      )
      const json: MissedOffers = yield res.json()
      if (json) {
        this.missedOffers = json
      }
    } catch (error) {
      this.status = 'error'
      throw error
    }
  }

  // This action is using SCAPI Shopper Baskets endpoint.
  *mergeBaskets(useStored: boolean) {
    try {
      this.status = StatusMap.PENDING

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-BasketMerge?usestored=${useStored}`,
        {
          method: 'POST',
          credentials: 'include',
          body: this.basketMergeData,
        }
      )

      if (!res.ok) {
        throw new Error('Error merging basket')
      }

      const json: {success: boolean, basket: ShopperBaskets.Basket} = yield res.json()

      if (json.success && json.basket) {
        this.getOrCreateBasket(json.basket)
      }

      this.status = StatusMap.IDLE
    } catch (error) {
      this.status = StatusMap.ERROR
    }
  }

  *clearSoftLogoutBasket() {
    try {
      if (!this.basket?.basketId) {
        throw MissingBasketError
      }

      this.status = StatusMap.PENDING

      this.rootStore.orderStore.recentOrder = null

      yield this.api.shopperBaskets.deleteBasket({
        parameters: {
          basketId: this.basket.basketId!,
        },
      })

      // Set postcode from cookie if exists
      this.setGuestPostalCode()

      this.status = StatusMap.DONE
    } catch (error) {
      this.status = StatusMap.ERROR
    }
  }

  buildThresholdItem(promoThreshold : BasketPromotionsThreshold){
    if(!promoThreshold.ID || !promoThreshold.enabled){
      return null
    }

    const newThresholdItem = {
      ID: promoThreshold.ID,
      enabled: promoThreshold.enabled,
      thresholdValue: promoThreshold.thresholdValue,
      thresholdSource: promoThreshold.thresholdSource,
      progressText: promoThreshold.progressText,
      completedText: promoThreshold.completedText,
      popoverTitle: promoThreshold.popoverTitle,
      popoverText: promoThreshold.popoverText,
      popoverLink: promoThreshold.popoverLink,
      popoverLinkText: promoThreshold.popoverLinkText,
      showOnComplete: promoThreshold.showOnComplete,
      svg: promoThreshold.svg,
      remaining: 0,
      complete: false,
      progress: 0
    }

    /*
      For the specifically defined thresholds of minimum and free delivery any threshold value will be overridden to ensure they are in line with site
      and predefined keywords will be substituted with dynamic values
    */
    
    const deliveryCharge = this.basketDeliveryCharge.toFixed(2).replace(/\.00/,'')

    if(newThresholdItem.thresholdSource){
        if(newThresholdItem.thresholdSource === 'minDelivery' || newThresholdItem.thresholdSource === 'freeDelivery') {
            const freeDeliveryThreshold = this.basketFreeDeliveryThreshold || 0;
            if(newThresholdItem.thresholdSource === 'minDelivery') {
              newThresholdItem.thresholdValue = newThresholdItem.thresholdValue
            } else if(newThresholdItem.thresholdSource === 'freeDelivery') {
                if(!freeDeliveryThreshold){
                  return null
                }
                if(newThresholdItem.popoverText) {
                  newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{freeDeliveryThreshold}',freeDeliveryThreshold.toFixed(2).replace(/\.00/,''))
                  newThresholdItem.thresholdValue = freeDeliveryThreshold
                }
            }
            if(newThresholdItem.popoverText){
              if(deliveryCharge != 0){
                newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{charge}', deliveryCharge)
              }
              else {
                newThresholdItem.popoverText = newThresholdItem.popoverText.replace('£{charge} delivery or', '')
              }
            }
        } else if (newThresholdItem.thresholdSource === 'smallBasketChargeAndDeliveryCharge' || newThresholdItem.thresholdSource === 'smallBasketChargeAndFreeDelivery') {
            const smallBasketChargeThreshold = this.basketSmallBasketChargeThreshold
            const smallBasketCharge = this.basketSmallBasketCharge
            if(!smallBasketChargeThreshold || !smallBasketCharge) {
              return null
            }

            if (newThresholdItem.thresholdSource === 'smallBasketChargeAndDeliveryCharge') {
                if(deliveryCharge == 0) {
                  return null
                }
                if(newThresholdItem.progressText){
                  newThresholdItem.progressText = newThresholdItem.progressText.replace('{charge}', `£${deliveryCharge}`)
                }
                if(newThresholdItem.completedText){
                  newThresholdItem.completedText = newThresholdItem.completedText.replace('{charge}', deliveryCharge)
                }
            }

            if (newThresholdItem.thresholdSource === 'smallBasketChargeAndFreeDelivery') {
                if(deliveryCharge > 0) {
                  return null
                }
                if(newThresholdItem.progressText){
                  newThresholdItem.progressText = newThresholdItem.progressText.replace('{smallBasketChargeThreshold}', `£${smallBasketChargeThreshold.toFixed(2)}`)
                }
            }
            if(newThresholdItem.popoverText){
              newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{smallBasketChargeThreshold}',smallBasketChargeThreshold.toFixed(2).replace(/\.00/,''))
              newThresholdItem.popoverText = newThresholdItem.popoverText.replace('{smallBasketCharge}',smallBasketCharge.toFixed(2).replace(/\.00/,''))
            }
            newThresholdItem.thresholdValue = smallBasketChargeThreshold;
            
        }
    }

    newThresholdItem.progress = Math.min((100 / newThresholdItem.thresholdValue) * (this.basket?.productTotal || 0), 100)
    newThresholdItem.complete = (newThresholdItem.progress === 100)

    if(newThresholdItem.complete && !newThresholdItem.showOnComplete){
      return null
    }
    newThresholdItem.remaining = (newThresholdItem.thresholdValue - (this.basket?.productTotal|| 0))
    if(newThresholdItem.progressText){
      const price = newThresholdItem.remaining < 1 ? (newThresholdItem.remaining * 100).toFixed(0).toString() + 'p' : `£${newThresholdItem.remaining.toFixed(2)}`
      newThresholdItem.progressText = newThresholdItem.progressText.replace('{amt}', price)
    }
    return newThresholdItem
  }

  *transformPromotionalThresholds() {
    const promoThresholds = []

    if(this.promotionalThresholds?.length){
      for (const thresholdItem of this.promotionalThresholds) {
        const thresholdObject = this.buildThresholdItem(thresholdItem)
        if(thresholdObject != null){
          promoThresholds.push(thresholdObject)
        }
      }

      promoThresholds.sort(function(a,b){
        return a.thresholdValue > b.thresholdValue ? 1 : -1
      });

      //ensure only up to first unfulfilled threshold returned to view
      let lastRelaventMessageIndex = 0;
      for(let i = 0; i < promoThresholds.length; i++){
          lastRelaventMessageIndex = i
          if(!promoThresholds[i].complete){
              break
          }
      }

      this.filteredPromotionalThresholds = promoThresholds.slice(0,lastRelaventMessageIndex + 1)

    }
  }

  *getBasketPromotionalThreshold() {
    try {
      if(!this.promotionalThresholds?.length){
        const res: Response = yield fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Basket-GetPromoThresholds`,
          {credentials: 'include'}
        )
        const json: BasketPromotionsThresholdResponse = yield res.json()
        if (json && json?.promoThresholds?.length) {
          this.promotionalThresholds = json.promoThresholds
        }
      }
      if(this.promotionalThresholds?.length){
        yield this.transformPromotionalThresholds()
      }
    } catch (error) {
      this.status = StatusMap.ERROR
      throw error
    }
  }

  *handlePostcodeDeliveryStatus(postcode: string) {
    try {
      this.processingAppliedPostCode = true
      const response: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliveryStatus?postcode=${encodeURIComponent(
          postcode
        )}`,
        {
          credentials: 'include',
        }
      )

      if (!response.ok) throw new Error(`Error during getting Delivery status for ${postcode}`)
      
      const json: {
        data: any
      } = yield response.json()

      if (typeof json?.data?.code !== 'boolean') {
        return false
      }

      // if postcode is not deliverable, set nearest store
      if (!json?.data?.code) {
        if(json?.data?.nearestStore) {
          // Set store from response
          console.log(`Setting nearest store to ${postcode} from delivery status`)
          this.setNearestStore(json.data.nearestStore)
        }
        else {
          // Fallback to google maps geocode if we couldn't find a matching store
          this.assignNearestStore(postcode)
        }
      }

      return json.data.code
    } catch (error) {
      console.error(error)
      return false
    } finally {
      this.processingAppliedPostCode = false
    }
  }

  expandDeliveryAddressForm(formType: AddressFormType) {
    if(formType != this.deliveryAddressFormExpanded){
      this.deliveryAddressFormExpanded = formType
    }
  }
}

// @TODO: Investigate if we need this class and transform cart items in transformBasket mapper
export class ProductItem extends Wrapper<TProductItem>() {
  // basketStore: BasketStore

  constructor(basketStore: BasketStore, productItem: TProductItem) {
    super(productItem)
  }
}

export const transformBasket = (basketStore: BasketStore, basket: CustomerBasket): StoreBasket => {
  const transformedBasket = {...basket}
  if (!transformedBasket.productItems) {
    transformedBasket.productItems = []
    return transformedBasket as StoreBasket
  }
  transformedBasket.productItems =
    transformedBasket.productItems.map((item) => new ProductItem(basketStore, item)) || []

  return transformedBasket as StoreBasket
}

const sanitizeOCAPIBasketFromIncorrectProperties = (basket: ShopperBaskets.Basket) => {
  const basketObj = {...basket} || {}
  const blackList = ['applicableShippingMethods', 'defaultShippingMethodId']

  for (const prop of blackList) {
    if (prop in basketObj) {
      delete basketObj[prop]
    }
  }

  return basketObj
}
